import {
	ActiveAnnouncementsBanner,
	BrandAttributionFooter,
} from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useCallback } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useDisplaySettings } from '../../hooks/useDisplaySettings';
import { useIsKeyboardLikelyOpen } from '../../hooks/useIsKeyboardLikelyOpen';
import { usePortalAuth } from '../../hooks/usePortalAuth';
import { getPortalAnnouncements } from '../../services/portalAnnouncementsService';
import { usePortalAuthStore } from '../../stores/portalAuthStore';
import { usePortalSiteStore } from '../../stores/portalSiteStore';
import { GetAppBar } from '../GetAppBar';
import { MenuDrawer } from '../MenuDrawer';
import { PullToRefresh } from '../PullToRefresh';
import { RouteFadeContainer } from '../RouteFadeContainer';
import { BottomNav } from './BottomNav';
import { PortalHeader } from './PortalHeader';

/**
 * Main layout wrapper for the e-commerce portal.
 *
 * Mobile (< 768px):
 *   - Sticky top header with search + cart + user menu
 *   - Scrollable content area
 *   - Fixed bottom tab bar for navigation
 *
 * Desktop (>= 768px):
 *   - Sticky top header with search + cart + user menu
 *   - Scrollable content area (full width — no sidebar)
 *   - Navigation lives in the header and bottom nav
 *
 * Note: The sidebar is not part of the global layout. Pages that need
 * a filter panel (e.g. catalog) render it themselves as a page-level concern.
 *
 * Access rules enforced here so every nested route inherits them:
 *   - Authenticated user with no active client → /select-client.
 *   - Unauthenticated visitor on a storefront with anonymous browsing
 *     OFF → bounce to `/` so the HomePage's Sign In dead-end shows.
 *     The catalog (and anything else under PortalLayout) must not render
 *     in that state — even a hand-typed `/catalog` URL has to redirect.
 */
export function PortalLayout(): JSX.Element {
	const { isAuthenticated, needsClientSelection } = usePortalAuth();
	const isRestoringSession = usePortalAuthStore((s) => s.isRestoringSession);
	const authToken = usePortalAuthStore((s) => s.token);
	// Memoised so the banner's effect doesn't re-run (and re-fetch) on every
	// layout render; it only needs to change when the session token does.
	const loadAnnouncements = useCallback(
		async () => getPortalAnnouncements(authToken),
		[authToken],
	);
	const isPortalSiteResolved = usePortalSiteStore((s) => s.isResolved);
	const display = useDisplaySettings();
	const location = useLocation();
	// When the on-screen keyboard is up the BottomNav unmounts (see
	// BottomNav.tsx). Drop the matching pb-[72px] padding so the cart /
	// product detail / form pages don't end up with a 72px dead zone at
	// the bottom while the user is typing.
	const keyboardOpen = useIsKeyboardLikelyOpen();

	// Hold off on every routing decision below while EITHER the initial
	// session-restore OR the public portal_site bootstrap is still
	// pending.
	//
	// Without the session-restore gate, refreshing any deep link (e.g.
	// /orders) would race the async /auth/me call: the first render
	// sees `isAuthenticated=false` and the locked-out branch below
	// would Navigate to `/`, replacing the URL before the restore can
	// finish — HomePage then bounces the now-authenticated user to
	// /catalog.
	//
	// Without the bootstrap gate, hand-typing /catalog (or following a
	// dashboard tile click before the bootstrap has answered) would
	// race the `/api/portal/site` call: useDisplaySettings falls back
	// to a hard-coded default with `isAnonymousVisitor: false` while
	// the bootstrap is in flight, so the locked-out branch sees
	// "anon, anon-browsing-disabled, not on /" → Navigate to `/` →
	// HomePage's anon-browsing-enabled fallback then bounces to
	// `/dashboard`. Net effect: every full-page-load of /catalog ended
	// up on /dashboard.
	if (isRestoringSession || !isPortalSiteResolved) {
		return <div className="h-dvh bg-brand-page-bg" />;
	}

	// Authenticated but no active client → must select before proceeding
	if (isAuthenticated && needsClientSelection) {
		return <Navigate to="/select-client" replace />;
	}

	// Locked-out visitor: anon browsing disabled AND no session. Send
	// them to the Sign In dead-end at `/`. Skip when already on `/` so
	// HomePage can render (and to avoid an infinite redirect).
	if (
		!isAuthenticated &&
		!display.isAnonymousVisitor &&
		location.pathname !== '/'
	) {
		return <Navigate to="/" replace />;
	}

	// Three layout modes:
	//   1. Welcome state — no session AND anon browsing disabled, on `/`:
	//      fully chrome-less AND edge-to-edge. The HomePage paints a full-
	//      bleed brand image and we drop the safe-area-top inset so the
	//      image runs under the translucent iOS status bar.
	//   2. Anonymous shopping — no session AND anon browsing enabled:
	//      regular header (with Sign In CTA via the BottomNav's Profile
	//      tab) + bottom nav (Cart hides itself when not authed; Profile
	//      pops the login modal). Bypassing safe-area on iOS here would
	//      let the catalog filter row sit under the dynamic island.
	//   3. Authenticated — full chrome (header + bottom nav + safe area
	//      + body bottom padding for the bottom-nav bar).
	//
	// We render the BottomNav for both authenticated AND anon-browsing
	// users on mobile — without it, anon users on small screens have no
	// "Sign In" affordance (the desktop UserMenu is hidden < md). The
	// nav's own auth-aware logic decides which tabs to expose.
	const isWelcomeState = !isAuthenticated && !display.isAnonymousVisitor;
	const showHeader = !isWelcomeState;
	const showBottomNav = isAuthenticated || display.isAnonymousVisitor;
	const reserveSafeAreaTop = !isWelcomeState;

	return (
		// `min-h-dvh` (NOT `h-dvh`) so the wrapper grows with content.
		// The previous `h-dvh + <main overflow-y-auto>` layout locked
		// the wrapper to viewport height and shoved scrolling inside
		// <main> — works on touch devices but breaks desktop mouse-
		// wheel scrolling on long pages (the dashboard with carousel +
		// categories + 2-up + item rows + grid is well over 2000px;
		// users couldn't see anything past the hero). PortalHeader is
		// already `sticky top-0` and BottomNav is `fixed bottom-0
		// md:hidden`, so both stay anchored without needing a captive
		// scroll region.
		<div
			// Outer wrapper bg = brand-primary so any sliver of viewport
			// not painted by the sticky header (e.g. the welcome state
			// where the header is hidden) still reads the chrome colour
			// rather than the off-white body tint. When the header IS
			// shown, it now owns its own `paddingTop: env(safe-area-
			// inset-top)` (so its bg-brand-primary extends up under the
			// iOS Dynamic Island / status bar even while the page
			// scrolls underneath the sticky element). PortalLayout no
			// longer applies the safe-area inset itself — leaving it on
			// the wrapper combined with `sticky top-0` on the header
			// produced a thin transparent strip above the header during
			// scroll on Capacitor iOS.
			className="min-h-dvh flex flex-col bg-brand-primary"
			style={
				reserveSafeAreaTop && !showHeader
					? { paddingTop: 'env(safe-area-inset-top)' }
					: undefined
			}
		>
			{showHeader ? <PortalHeader /> : null}
			{/* PD-16558 — phone-only app prompt; renders nothing on larger
			    screens, inside the app, without an app, or while snoozed. */}
			{showHeader ? <GetAppBar /> : null}
			{/* Announcements published with Location = "Ecommerce" (PD-14790).
			    Sits under the sticky header and above the route content, which
			    is where the legacy storefront put them. Suppressed on the
			    welcome / sign-in screen, which is deliberately full-bleed and
			    which legacy also skipped. */}
			{showHeader ? (
				<ActiveAnnouncementsBanner
					location="ecommerce"
					fetchAnnouncements={loadAnnouncements}
				/>
			) : null}
			{/* Tenant-tinted body bg — `bg-brand-page-bg` reads from the
			    --brand-page-bg var that BrandThemeApplier mixes from the
			    active primaryColor. Falls back to a warm off-white for
			    un-branded tenants so the storefront never reads as a flat
			    gray dashboard. Dark mode keeps the near-black neutral. */}
			{/* Bottom padding reserves the FULL footprint of the fixed
			    BottomNav: 48px of tab row + the nav's trimmed inset padding
			    (inset − 12px) + ~24px breathing room so the last content row
			    never sits flush against the bar (PD-14604). 60px = 48 + 24
			    − the 12px the nav now overlaps into the home-indicator zone. */}
			<main
				className={`flex-1 flex flex-col bg-brand-page-bg ${
					showBottomNav && !keyboardOpen
						? 'pb-[calc(60px+env(safe-area-inset-bottom))] md:pb-0'
						: ''
				}`}
			>
				{/* Route-fade prototype (PD-14696) — PUSH-only ~110ms fade-in;
				    unmount this wrapper (leave <Outlet/>) to drop the effect. */}
				{/* `main` is a flex column and the route wrapper grows inside
				    it, so a full-bleed route (the welcome/sign-in hero) can
				    fill exactly the height left over after the header and the
				    attribution footer. Previously the hero claimed a full
				    100dvh of its own, which pushed the footer below the fold
				    and produced a scrollbar the height of the footer on a page
				    with nothing to scroll (PD-14409).

				    `[&>*]:w-full` is load-bearing. Every page roots itself in a
				    `max-w-* mx-auto` container, which is block-flow idiom: fill
				    the parent, cap at the max-width, centre the leftover. As a
				    FLEX ITEM that idiom inverts — auto margins resolve on the
				    cross axis and take precedence over `align-items: stretch`,
				    so the container shrink-wraps its content instead of growing
				    to the cap. Measured on /catalog at a 1440px viewport: the
				    `max-w-6xl` (1152px) page rendered 477px wide, which is what
				    made the desktop storefront read as a stretched phone. An
				    explicit `width: 100%` restores the block-flow behaviour
				    while leaving the flex column (and the hero's `flex-1`)
				    intact. */}
				<RouteFadeContainer className="flex-1 flex flex-col [&>*]:w-full">
					<Outlet />
				</RouteFadeContainer>
			</main>
			{/* "Powered by X" platform attribution (PD-14113). The shared
			    component hides itself below the md breakpoint, so it only shows
			    at tablet/desktop widths where the fixed BottomNav isn't on the
			    bottom edge — matching the CRM (no room above the mobile nav).
			    bg-brand-page-bg pins it to the NEUTRAL page surface (off-white in
			    light, near-black in dark) instead of the brand-primary wrapper, so
			    the theme-keyed logo (dark wordmark light / white wordmark dark)
			    keeps good contrast and the bar is never the active brand color.
			    Border + text ride the same brand family (PD-15245): with only the
			    background overridden, the component's CRM-gray separator and copy
			    sat mismatched on brand-tinted dark themes. brand-border/brand-muted
			    are the exact tokens every card on the page pairs with this bg. */}
			<BrandAttributionFooter className="bg-brand-page-bg border-brand-border text-brand-muted" />
			{showBottomNav ? <BottomNav /> : null}
			{showBottomNav ? <MenuDrawer /> : null}
			{/* Pull-to-refresh (PD-14694) — one mount covers every window-
			    scroll surface under the layout; the component itself is
			    touch-only and inert on desktop pointers. */}
			<PullToRefresh />
		</div>
	);
}
